"use client";

import { useAuth } from "@clerk/nextjs";
import { useState } from "react";

const API_URL = process.env.NEXT_PUBLIC_API_URL;

export default function AuthApiCheck() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<unknown>(null);
  const [error, setError] = useState("");

  async function testApi() {
    setLoading(true);
    setError("");
    setResult(null);

    try {
      if (!API_URL) {
        throw new Error("NEXT_PUBLIC_API_URL is not configured.");
      }

      const token = await getToken();

      if (!token) {
        throw new Error("No Clerk session token is available. Please sign in.");
      }

      const response = await fetch(`${API_URL}/auth/me`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || data.error || `API request failed (${response.status})`
        );
      }

      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="font-bold text-slate-900">
        Webspider AI API Connection
      </h2>

      <p className="mt-1 text-sm text-slate-500">
        Verify your signed-in Clerk session against the development API.
      </p>

      <div className="mt-4">
        {!isLoaded ? (
          <p className="text-sm text-slate-500">Loading authentication...</p>
        ) : !isSignedIn ? (
          <p className="text-sm text-amber-700">
            Please sign in to test the authenticated API connection.
          </p>
        ) : (
          <button
            type="button"
            onClick={testApi}
            disabled={loading}
            className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? "Testing connection..." : "Test authenticated API"}
          </button>
        )}
      </div>

      {error && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {result !== null && (
        <div className="mt-4">
          <p className="mb-2 text-sm font-semibold text-emerald-700">
            Authenticated API response
          </p>
          <pre className="overflow-x-auto rounded-lg bg-slate-950 p-4 text-xs text-green-300">
            {JSON.stringify(result, null, 2)}
          </pre>
        </div>
      )}
    </section>
  );
}
